import { useCallback, useEffect, useMemo, useState } from 'react'
import { Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import TableRowsIcon from '@mui/icons-material/TableRows'
import { apiGet } from '../../generic/Api_Functions'
import { getBomVersionStructure } from '../bomLibrary/bomLibraryApi'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import ImportFile from '../../importFile'
import BomTreeView from './BomTreeView'
import BomEngineeringIdentityCard from './BomEngineeringIdentityCard'
import WorkflowBomLinkHeader from './WorkflowBomLinkHeader'
import PublishAfterImportDialog from './PublishAfterImportDialog'
import { resolveBomEngineeringIdentity } from './bomEngineeringIdentity'
import { collectJobAssignments, getJobAssignmentsFor } from './bomTree'
import { resolveBomImportMapping } from './bomImportMapping'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useSmartInterval } from '../../generic/hooks/useSmartInterval'
import { areObjectValuesEqual, buildChangeMgmtToolbar, computeChangeMgmtValues, useWorkflowResourceExtendedColumns } from './ResourcesExtendedColumnsManagement'

/**
 *
 * @param {object} props
 * @param {import('../../generic/utilities/objectStructures/workflow/workflow').Workflow} props.workflow Workflow id
 * @param {string} props.workflow Temporally generated workflow uuid
 * @param {boolean} props.editing If true, workflow is on edit mode
 * @param {function(boolean)} props.updateHasWorkflowResources to update hasWorkflowResources field
 * @param {function(import('../../generic/utilities/objectStructures/workflow/workflow').Workflow, import('../../generic/utilities/objectStructures/workflow/workflow').WorkflowResource[], import('../../generic/utilities/objectStructures/workflow/workflow').WorkflowResource[])} props.resetResources
 * to re-map previously assigned resources onto the newly imported BOM (HU-13/S39-S44): called with
 * the current workflow, the newly imported flat component list, and the component list as it was
 * right before the import (used to resolve each assigned component's code+parent ComponentKey)
 * @param {function(import('../../generic/utilities/objectStructures/workflow/workflow').BillOfMaterials)} props.updateBillOfMaterials to update workflow bill of materials
 * @param {function(import('./bomTree').JobAssignment, string)} props.onHighlightJob S31/S32: called
 * when a job chip (table or tree) is clicked, with the assignment and the component's own `uuid`
 * @param {string} props.highlightedResourceUUID S31/S32: the component whose chip produced the
 * current graph highlight, marked in the tree with the node's own blue
 * @param {function()} props.onClearHighlight S31/S32: called when a click lands outside the tree, to
 * drop that selection
 * @param {function(Set<string>, string)} props.onHighlightAssembly S69/S70 (historia 152,
 * RN-02.6): called when an assembly is selected in the TREE view, with the node ids of every
 * operation that consumes any of its components and the assembly's own `uuid`. Only the tree
 * offers it: the table has no hierarchy to select an assembly from (CA2.3)
 * @param {import('../../generic/utilities/objectStructures/workflow/workflow').WorkflowResource[]} props.removedResources
 * HU-13/S44: components of the previous BOM with no counterpart in the newly imported one. The
 * backend only ever returns the BOM currently loaded, so they would silently disappear from the
 * view; they are kept (flagged `deleted`) so the removal stays visible — in red — next to the
 * imported components, exactly like the job's Resources (BOM) table already shows it. Owned by the
 * workflow editor, NOT by this component: the BOM accordion mounts with `unmountOnExit`, so local
 * state here would be wiped every time the user collapses it, selects a JOB node or opens a
 * subworkflow, and the removal would silently vanish from the view.
 * @param {function(import('../../generic/utilities/objectStructures/workflow/workflow').WorkflowResource[])} props.onRemovedResourcesChange
 * called with the components the last import removed
 * @param {boolean} props.canReadBomLibrary story 117 (S73/S92): whether the user holds `BOM_LIBRARY`
 * or `BOM_LIBRARY_EDIT`. Passed in rather than read here from `useAuthorization` so this component
 * keeps being mountable in isolation, and — more to the point — so a user with NEITHER permission
 * never triggers the link read (N15 would answer 403).
 * @param {boolean} props.canEditBomLibrary story 117 (S90/S111): whether the user holds
 * `BOM_LIBRARY_EDIT`
 * @param {object} props.pendingBomLink story 117 (S71, corrección PO 2026-09-09): the library BOM
 * version chosen in this editing session and not written yet. While it lasts the components on
 * screen are ITS components (N3), because that is what the workflow will hold once it is saved.
 * @param {function(object)} props.onBomLinkSelected story 117 (S71): handed to the header, which
 * asks for the discard confirmation and reports the choice to the editor. S135 (corrección PO
 * 2026-09-10): a move to a newer version of the ALREADY linked BOM travels the same way, with
 * `mode: 'UPDATE'`, but only after the assignments have been re-mapped here (see
 * `onBomUpdateSelected` below)
 * @param {function(object)} props.onBomLinkChange story 117 (S86): called with the workflow's
 * `WorkflowBomLink` whenever it is (re)read, so the editor can warn before the save that would
 * create version n+1 of the linked BOM
 * @returns
 */
const WorkflowResource = ({ workflow, editing, updateHasWorkflowResources, resetResources, updateBillOfMaterials, onHighlightJob = () => {}, onHighlightAssembly = () => {}, highlightedResourceUUID = '', onClearHighlight = () => {}, removedResources = [], onRemovedResourcesChange = () => {}, canReadBomLibrary = false, canEditBomLibrary = false, pendingBomLink = {}, onBomLinkSelected = () => {}, onBomLinkChange = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const { changeMgmtColumns, withExtendedColumns } = useWorkflowResourceExtendedColumns(t_)
  // Story 117: the workflow's BOM link, as the header reads it (N15). It decides whether the
  // structure may be edited from here (S90) and what has to happen after an import (S113).
  const [bomLink, setBomLink] = useState({})
  // Story 117: bumped whenever a library operation (link, publish, update) changed the structure
  // the editor is showing, to force the same reload the temp scope changes already force.
  const [structureRefreshKey, setStructureRefreshKey] = useState(0)
  const [publishAfterImportOpen, setPublishAfterImportOpen] = useState(false)

  // S25 (RN08.1): the tree view is the default; S26: the switch reveals the existing table,
  // unchanged.
  const [viewMode, setViewMode] = useState('tree')
  const [treeItems, setTreeItems] = useState([])
  // HU-13/S44: see `removedResources` in the JSDoc above — the removed components are owned by the
  // workflow editor because this component is unmounted whenever its accordion collapses.
  const removedItems = removedResources

  const [changeMgmtValues, setChangeMgmtValues] = useState({})

  // S90 (RN-05.7): with a LINKED BOM, editing the structure means creating a new version of a
  // library BOM, which needs `BOM_LIBRARY_EDIT`. Without it the structure is read-only — the
  // assignment of components to jobs is NOT affected, it belongs to the workflow, not to the BOM.
  const structureReadOnly = Boolean(bomLink.linked) && !canEditBomLibrary
  // S71 (corrección PO 2026-09-09): with a link waiting to be applied, importing would mix a temp
  // load with a structure that is about to be replaced by the linked version — it stays disabled
  // until the save resolves the link.
  const hasPendingBomLink = Boolean(pendingBomLink?.bomId)
  const structureEditable = editing && !structureReadOnly && !hasPendingBomLink

  // S28/S29/S30 (RN09.1-RN09.3): job assignments derived from the workflow's own node tree,
  // recursively (S31/S32), reused by both the table's "Jobs" column and the tree view's chips.
  const jobAssignmentsByUUID = useMemo(() => collectJobAssignments(workflow?.nodes), [workflow?.nodes])

  const columns = [
    {
      field: 'engItem',
      name: t_('Engineering item')
    },
    {
      field: 'engRevision',
      name: t_('Engineering revision'),
      readOnly: true
    },
    {
      field: 'resourceTypeName',
      name: t_('Type'),
      render: (row) => {
        let message = ''
        if (row.resourceTypeName === 'RAW_MATERIAL_REFERENCE') {
          message = t_('Raw material')
        }
        if (row.resourceTypeName === 'PART_REFERENCE') {
          message = t_('Part reference')
        }
        return <Typography color={row.deleted ? 'error.main' : null}>{message}</Typography>
      }
    },
    {
      field: 'resource.code',
      name: t_('Code')
    },
    {
      field: 'resource.name',
      name: t_('Name')
    },
    {
      field: 'position',
      name: t_('position')
    },
    {
      field: 'revision',
      name: t_('Revision')
    },
    {
      field: 'category',
      name: t_('Category')
    },
    {
      field: 'componentType',
      name: t_('Component type')
    },
    {
      field: 'quantity',
      name: t_('Quantity')
    },
    {
      field: 'remaining',
      name: t_('Remaining'),
      render: (row) => {
        const color = row.deleted ? 'error.main' : getColorForRemaining(row.remaining)
        return (<Typography color={color}>{floatToStringWithMaxDecimal(row.remaining)}</Typography>)
      }
    },
    {
      field: 'jobs',
      name: t_('Jobs'),
      // S28/S29/S30 (RN09.1-RN09.3, ajuste PO 23/07/2026): in the table view the assigned jobs
      // reuse the standard multi-option visualizer (same as PlannerDetails' toolReferences); the
      // row carries the job codes as simple data (filled in dataFetch). An unassigned component
      // still shows the explicit "Not assigned" indication (S30). The click-to-highlight chips
      // stay in the tree view (S31/S32).
      type: 'smartMultiSelect',
      isSimpleData: true,
      readOnly: true
    }
  ]

  // HU-13/S44: a component removed by the last import is rendered in red across every column, the
  // same convention the job's Resources (BOM) table uses for a deleted assignment.
  const getColumns = () => withExtendedColumns(columns).map((column) => ({
    ...column,
    color: (row) => (row?.deleted ? 'error.main' : null)
  }))

  const computeChangeMgmtValuesForRows = useCallback((rows) => {
    return computeChangeMgmtValues({ rows, changeMgmtColumns })
  }, [changeMgmtColumns])

  const billOfMaterials = useMemo(() => workflow?.billOfMaterials || {}, [workflow?.billOfMaterials])
  const updateBillOfMaterialsField = useCallback((field, value) => {
    updateBillOfMaterials?.({
      ...billOfMaterials,
      [field]: value
    })
  }, [billOfMaterials, updateBillOfMaterials])

  const refreshImportedBillOfMaterials = useCallback(async () => {
    if (!workflow?.tempWorkflowUUID) {
      return
    }
    const importedBillOfMaterials = await apiGet('billOfMaterials/temp/' + workflow.tempWorkflowUUID)
    if (!hasBillOfMaterialsData(importedBillOfMaterials)) {
      return
    }
    const nextBillOfMaterials = mergeImportedBillOfMaterials(billOfMaterials, importedBillOfMaterials)
    if (areBillOfMaterialsEqual(billOfMaterials, nextBillOfMaterials)) {
      return
    }
    updateBillOfMaterials?.(nextBillOfMaterials)
  }, [billOfMaterials, updateBillOfMaterials, workflow?.tempWorkflowUUID])

  const extendedColumnsToolbar = useMemo(() => {
    return buildChangeMgmtToolbar({ changeMgmtColumns, changeMgmtValues })
  }, [changeMgmtColumns, changeMgmtValues])

  const bomHeaderToolbar = useMemo(() => {
    return {
      render: {
        fetchFunction: () => (
          <Stack direction='row' spacing={2} alignItems='center'>
            <TextField
              label={t_('Version')}
              value={billOfMaterials.version ?? ''}
              onChange={(event) => updateBillOfMaterialsField('version', event.target.value)}
              disabled={!structureEditable}
            />
            <TextField
              label={t_('Index')}
              value={billOfMaterials.indexValue ?? ''}
              onChange={(event) => updateBillOfMaterialsField('indexValue', event.target.value)}
              disabled={!structureEditable}
            />
            <TextField
              label={t_('Revision BOM')}
              value={billOfMaterials.revisionBom ?? ''}
              onChange={(event) => updateBillOfMaterialsField('revisionBom', event.target.value)}
              disabled={!structureEditable}
            />
            {extendedColumnsToolbar?.render?.fetchFunction?.()}
          </Stack>
        )
      }
    }
  }, [billOfMaterials, structureEditable, extendedColumnsToolbar, t_, updateBillOfMaterialsField])

  // Every row the section displays needs `remaining` (quantity not yet assigned to a job) and `jobs`
  // (the assignment chips / column). Applied to the rows read by `dataFetch` AND to the rows a
  // pending library update swaps in (S135): the table prints `remaining` with `toFixed`, so a row
  // without it crashes the render.
  const attachRemainingAndJobs = useCallback((rows) => {
    const assignmentsByUUID = collectJobAssignments(workflow?.nodes)
    const jobNodes = workflow?.nodes?.filter(node => node.nodeType === 'JOB') || []
    rows.forEach(workflowResource => {
      let remaining = Number(workflowResource.quantity) || 0
      jobNodes.forEach(node => {
        const resources = node.jobReference?.resources?.filter(resource => resource.resourceQuantityBOM?.workflowResourceUUID === workflowResource.uuid)
        resources?.forEach(resource => {
          remaining = remaining - (Number(resource.quantityFirstUnit) || 0)
        })
      })
      workflowResource.remaining = Math.round(remaining * 10000) / 10000
      // S28/S30 (ajuste PO): job codes as simple data for the smartMultiSelect Jobs column;
      // unassigned components keep their explicit indication.
      const jobCodes = getJobAssignmentsFor(workflowResource.uuid, assignmentsByUUID).map(assignment => assignment.jobCode)
      workflowResource.jobs = jobCodes.length > 0 ? jobCodes : [t_('Not assigned')]
    })
    return rows
  }, [workflow?.nodes, t_])

  const dataFetch = useCallback(async () => {
    if (workflow?.tempWorkflowUUID) {
      /** @type {import('../../generic/utilities/objectStructures/workflow/workflow').WorkflowResource[]} */
      let data
      // S71: the chosen version's own structure (N3) — the same `WorkflowResourceDto[]` the workflow
      // endpoints return (contrato-sun.md N3), so everything below is computed exactly as always.
      if (pendingBomLink?.bomId) {
        data = await getBomVersionStructure(pendingBomLink.bomId)
      } else if (workflow?.id) {
        data = await apiGet('workflowResource/' + workflow?.tempWorkflowUUID + '/' + workflow.id)
      } else {
        data = await apiGet('workflowResource/' + workflow?.tempWorkflowUUID)
      }
      await refreshImportedBillOfMaterials()
      attachRemainingAndJobs(data)
      if (changeMgmtColumns && changeMgmtColumns.length > 0) {
        const nextValues = computeChangeMgmtValuesForRows(data)
        setChangeMgmtValues(prev => (areObjectValuesEqual(prev, nextValues) ? prev : nextValues))
      }
      return data
    }
    return []
  }, [workflow, pendingBomLink?.bomId, refreshImportedBillOfMaterials, attachRemainingAndJobs, changeMgmtColumns, computeChangeMgmtValuesForRows])

  // S25/S27 (RN08.1/RN08.3): the tree view fetches its own flat component list (same query as the
  // table) to build the position-based hierarchy client-side. Keyed on the same identity the
  // table already uses (`forceLoad`), not on `dataFetch`'s own identity — `dataFetch` is
  // recreated on every render because of `changeMgmtColumns` (extended columns hook), so keying
  // on it directly would re-fetch (and re-render) in a loop.
  const forceLoadKey = `${workflow?.id ?? ''}-${workflow?.tempWorkflowUUID ?? ''}-${workflow?.hasWorkflowResources ?? ''}-${editing}-${structureRefreshKey}-${pendingBomLink?.bomId ?? ''}`
  useEffect(() => {
    let cancelled = false
    dataFetch().then((result) => {
      if (!cancelled) {
        setTreeItems(result)
      }
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceLoadKey])

  // S25/S27 + HU-13/S44: what the tree and the table render — the imported BOM plus the components
  // it removed, so a removal is visible instead of being a silently missing row.
  const displayedItems = useMemo(() => [...treeItems, ...removedItems], [treeItems, removedItems])

  // H112_S1/S6/S9: identidad de ingeniería del conjunto que la BOM describe. Se deriva de la misma
  // lista que pinta el árbol, así que una reimportación la reevalúa sola (S9). La lista ya llega
  // recortada a la última importación por el backend: aquí no se compara ninguna fecha (S8).
  const engineeringIdentity = useMemo(() => resolveBomEngineeringIdentity(displayedItems), [displayedItems])

  // The table fetches on its own (it is not fed from `treeItems`), so the removed components are
  // appended to its own result too; its reload key carries WHICH components were removed (not how
  // many: two consecutive imports each removing a single, different component would otherwise
  // leave the table showing the first one).
  const tableDataFetch = useCallback(async () => {
    const importedItems = await dataFetch()
    return [...importedItems, ...removedItems]
  }, [dataFetch, removedItems])
  const tableForceLoadKey = `${forceLoadKey}-${removedItems.map((item) => item.uuid).join(',')}`

  const checkImportedTemp = async () => {
    if (!workflow?.tempWorkflowUUID) {
      return false
    }
    if (workflow?.hasWorkflowResources) {
      return true
    }
    // Story 117 (S71): a library version chosen but not saved yet IS a BOM for the editor (title
    // "Resources (BOM)", job palette in BOM mode), even though no temp load exists in Sun.
    if (pendingBomLink?.bomId) {
      return true
    }
    let data
    if (workflow?.id) {
      data = await apiGet('workflowResource/existsTemp/' + workflow?.tempWorkflowUUID + '/' + workflow.id)
      return data.value
    }
    data = await apiGet('workflowResource/existsTemp/' + workflow?.tempWorkflowUUID)
    return data.value
  }

  const smartInterval = useSmartInterval(checkImportedTemp, 1000, (value) => { updateHasWorkflowResources(value) })

  useEffect(() => {
    if (workflow?.hasWorkflowResources) {
      smartInterval(false)
    }
  }, [workflow?.hasWorkflowResources, smartInterval])

  // Shared by the table toolbar and the tree view's own import button (RN08 amendment,
  // PO 23/07/2026: the tree view must also allow importing a new BOM).
  // S135 (HU-07/CA7.3, corrección PO 2026-09-10): the user accepted moving to a newer version of
  // the linked BOM. Nothing is written — it is a pending change of the workflow, applied by the
  // general save (N20) — so the tab has to show the new structure NOW, with the assignments carried
  // over. That is exactly what an import already does, through the same code+parent re-mapping
  // (HU-13/S39-S44): the components come from the chosen version (N3) instead of from a temp load,
  // and everything else is the very same path. The choice is lifted to the editor last, so the
  // re-mapped assignments are dispatched before the pending link is.
  const onBomUpdateSelected = async (selection) => {
    const previousWorkflowResources = displayedItems
    const importedWorkflowResources = await getBomVersionStructure(selection.bomId)
    const mapping = resolveBomImportMapping(previousWorkflowResources, importedWorkflowResources)
    // The new rows are displayed straight away: they need `remaining`/`jobs` like any fetched row
    // (before the re-map every new component reads as fully unassigned, S135).
    setTreeItems(attachRemainingAndJobs(importedWorkflowResources))
    // S135/S44: a component the new version dropped stays visible (in red) instead of silently
    // disappearing, and its assignment is retired by the same mapping.
    onRemovedResourcesChange(previousWorkflowResources
      .filter((previousItem) => !mapping[previousItem.uuid])
      .map((previousItem) => ({ ...previousItem, deleted: true })))
    resetResources(workflow, importedWorkflowResources, previousWorkflowResources)
    onBomLinkSelected(selection)
  }

  const bomImportFileParams = [{
    bean: {
      beanDtoName: 'ImportWorkflowResource',
      beanDtoPackage: 'com.smartpm.sun.usecase.workflowresource.infrastructure.rest',
      beanName: 'ImportWorkflowResource',
      beanPackage: 'com.smartpm.sun.usecase.workflowresource.infrastructure.rest',
      serviceName: 'ImportWorkflowResourceCsv',
      servicePackage: 'com.smartpm.sun.usecase.workflowresource.infrastructure.csv'
    },
    name: t_('Workflow resource'),
    extraParams: new Map([['tempWorkflowUUID', workflow?.tempWorkflowUUID]]),
    closeDialogOnSuccess: true,
    postImportAction: async () => {
      // HU-13/S39-S44: `displayedItems` here still holds the BOM as it was right before the
      // import (this render's closure, not yet overwritten by `setTreeItems` below) —
      // it is the list `resetResources` needs to resolve each assigned component's
      // code+parent ComponentKey against. Components already removed by a previous import are
      // part of it too, so re-importing a BOM that brings one of them back re-links it (and
      // drops it from the removed list) instead of leaving a stale red row behind.
      const previousWorkflowResources = displayedItems
      await refreshImportedBillOfMaterials()
      const importedWorkflowResources = await dataFetch()
      // S44: whatever the mapping could not pair with an imported component is gone from the new
      // BOM — kept in the view, flagged, instead of vanishing.
      const mapping = resolveBomImportMapping(previousWorkflowResources, importedWorkflowResources)
      setTreeItems(importedWorkflowResources)
      onRemovedResourcesChange(previousWorkflowResources
        .filter((previousItem) => !mapping[previousItem.uuid])
        .map((previousItem) => ({ ...previousItem, deleted: true })))
      resetResources(workflow, importedWorkflowResources, previousWorkflowResources)
      // S113 (RN-06.8/D22): on a LINKED workflow the import cannot just sit there — the user has to
      // say whether it becomes a new version of that BOM or turns the BOM back into an embedded one.
      // Everything above is unchanged (S98): the decision is asked AFTER the re-import ran as always.
      if (bomLink.linked) {
        setPublishAfterImportOpen(true)
      }
    }
  }]

  return (
    <Stack spacing={2}>
      {/* Story 117 (S73/S92/S111/S122): where the components come from, and every library action on
          this workflow. It owns the link read (N15) and hands the result down through
          `onBomLinkChange`. */}
      <WorkflowBomLinkHeader
        workflowId={workflow?.id || 0}
        tempWorkflowUUID={workflow?.tempWorkflowUUID || ''}
        editing={Boolean(editing)}
        canReadLibrary={canReadBomLibrary}
        canEditLibrary={canEditBomLibrary}
        hasWorkflowResources={Boolean(workflow?.hasWorkflowResources)}
        pendingBomLink={pendingBomLink}
        onBomLinkSelected={onBomLinkSelected}
        onBomUpdateSelected={onBomUpdateSelected}
        refreshKey={`${workflow?.tempWorkflowUUID ?? ''}-${editing}`}
        onLinkChange={(link) => { setBomLink(link || {}); onBomLinkChange(link || {}) }}
        onStructureChange={() => setStructureRefreshKey((previous) => previous + 1)}
      />
      <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={2}>
        {bomHeaderToolbar.render.fetchFunction()}
        <Stack direction='row' alignItems='center' gap={1}>
          {/* RN08 (enmienda PO 23/07/2026): the tree view offers the same BOM import as the table
              (in table view the SmartMateriaUITable toolbar already renders its own button). */}
          {viewMode === 'tree'
            ? <ImportFile beanParams={bomImportFileParams} disabled={!structureEditable} />
            : null}
          {/* S25/S26 (RN08.1/RN08.4): tree is the default view; the switch reveals the existing
              table, unchanged, at both workflow and subworkflow level (aclaración GATE 2). */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size='small'
            onChange={(event, newViewMode) => {
              if (newViewMode) {
                setViewMode(newViewMode)
              }
            }}
          >
            <ToggleButton value='tree' data-testid='bom-view-toggle-tree' aria-label={t_('Tree view')}>
              <Tooltip title={t_('Tree view')}><AccountTreeIcon fontSize='small' /></Tooltip>
            </ToggleButton>
            <ToggleButton value='table' data-testid='bom-view-toggle-table' aria-label={t_('Table view')}>
              <Tooltip title={t_('Table view')}><TableRowsIcon fontSize='small' /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>
      {viewMode === 'tree'
        ? (
          <>
            {/* H112_S1 (CA1/CA3): la identidad del conjunto se publica SOLO en la vista de árbol —
                en la tabla ya son columnas — entre la cabecera editable y el árbol. Es de solo
                lectura (CA2) y no se pinta si la BOM no trae identidad (H112_S7/DEC-2). */}
            <BomEngineeringIdentityCard identity={engineeringIdentity} />
            <BomTreeView
              items={displayedItems}
              jobAssignmentsByUUID={jobAssignmentsByUUID}
              onHighlightJob={onHighlightJob}
              onHighlightAssembly={onHighlightAssembly}
              highlightedResourceUUID={highlightedResourceUUID}
              onClearHighlight={onClearHighlight}
            />
          </>
          )
        : (
          <SmartMateriaUITable
            dataFetch={tableDataFetch}
            forceLoad={tableForceLoadKey}
            columns={getColumns()}
            importFile={bomImportFileParams}
            importFileDisabled={!structureEditable}
          />
          )}
      {/* S113 (RN-06.8/D22): an import over a LINKED workflow demands an explicit decision about
          the link before anything else happens. */}
      <PublishAfterImportDialog
        open={publishAfterImportOpen}
        workflowId={workflow?.id || 0}
        tempWorkflowUUID={workflow?.tempWorkflowUUID || ''}
        link={bomLink}
        onPublished={() => { setPublishAfterImportOpen(false); setStructureRefreshKey((previous) => previous + 1) }}
        onKeptEmbedded={() => { setPublishAfterImportOpen(false); setStructureRefreshKey((previous) => previous + 1) }}
      />
    </Stack>
  )
}

const floatToStringWithMaxDecimal = (number, maxDecimals = 4) => {
  // Defensive: a row without `remaining` (or a non-numeric one) prints 0 instead of crashing.
  return parseFloat((Number(number) || 0).toFixed(maxDecimals))
}

const getColorForRemaining = (remaining) => {
  return remaining === 0 ? 'success.main' : (remaining < 0 ? 'error.main' : 'yellow.main')
}

const hasBillOfMaterialsData = (billOfMaterials) => {
  return Boolean(
    billOfMaterials &&
      (hasText(billOfMaterials.version) ||
        hasText(billOfMaterials.indexValue) ||
        hasText(billOfMaterials.revisionBom) ||
        hasText(billOfMaterials.comments))
  )
}

const areBillOfMaterialsEqual = (currentBillOfMaterials, importedBillOfMaterials) => {
  return ['version', 'indexValue', 'revisionBom', 'comments'].every(
    field => (currentBillOfMaterials?.[field] ?? '') === (importedBillOfMaterials?.[field] ?? '')
  )
}

const mergeImportedBillOfMaterials = (currentBillOfMaterials, importedBillOfMaterials) => {
  return {
    ...currentBillOfMaterials,
    version: hasText(importedBillOfMaterials.version) ? importedBillOfMaterials.version : currentBillOfMaterials.version,
    indexValue: hasText(importedBillOfMaterials.indexValue) ? importedBillOfMaterials.indexValue : currentBillOfMaterials.indexValue,
    revisionBom: hasText(importedBillOfMaterials.revisionBom) ? importedBillOfMaterials.revisionBom : currentBillOfMaterials.revisionBom,
    comments: hasText(importedBillOfMaterials.comments) ? importedBillOfMaterials.comments : currentBillOfMaterials.comments
  }
}

const hasText = (value) => {
  return Boolean(value?.trim?.())
}

export { WorkflowResource, getColorForRemaining, floatToStringWithMaxDecimal }
